<script setup lang="ts" name="LoginScreen">
  import OTPScreen from '@/components/OTPScreen.vue'
  import { AuthService } from '@/service/AuthService'
  import { useStore } from '@/utils/store'
  import { getErrorMessage } from '@/utils/utils'
  import { UIAlert, UIButton, UIForm, UIFormItem, UIInput, UITextMdNormal, useNotification } from '@gohighlevel/ghl-ui'
  import { ref } from 'vue'

  import TurnstileWidget from '@/components/TurnstileWidget.vue'
  import { AltType, TURNSTILE_TYPE, type TurnstileType } from '@/utils/const'
  import { AxiosError } from 'axios'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()
  const otpSent = ref(false)
  const store = useStore()
  const formRef = ref()
  const formValue = ref({
    email: '',
  })
  const contactId = ref('')

  const submitLoading = ref(false)

  const notification = useNotification()

  const turnstileWidgetRef = ref<{ reset: () => void } | null>(null)
  const turnstileToken = ref<string | undefined>(undefined)
  const turnstileType = ref<TurnstileType>(TURNSTILE_TYPE.INVISIBLE)
  const turnstileError = ref('')

  const emit = defineEmits(['onLogin'])
  const rules = {
    email: {
      required: true,
      trigger: ['input', 'blur'],
      validator: (_: unknown, value: string) => {
        if (!value) {
          return new Error(t('login.emailRequired'))
        }
        if (!/\S+@\S+\.\S+/.test(value)) {
          return new Error(t('login.emailInvalid'))
        }
        return true
      },
    },
  }

  const onTurnstileVerify = ({ token }: { token: string }) => {
    turnstileError.value = ''
    turnstileToken.value = token
  }

  const onTurnstileExpired = () => {
    turnstileToken.value = undefined
  }

  const switchToVisibleChallenge = () => {
    turnstileType.value = TURNSTILE_TYPE.NORMAL
    turnstileToken.value = undefined
    turnstileError.value = t('login.captchaPrompt')
    // TurnstileWidget watches `mode` and re-renders itself on change — no
    // imperative call needed here.
  }

  const onTurnstileError = () => {
    // Covers both failure paths the widget surfaces:
    //   1. Cloudflare api.js fails to load / times out (ad-blockers,
    //      locked-down networks) — the widget never renders.
    //   2. Cloudflare runtime error-callback fires after render (expired
    //      token, invalid sitekey, network blip mid-challenge) — the widget
    //      goes dead.
    // Either way, clear any stale token and show a single notification so
    // the user isn't stuck staring at a silent challenge that will never
    // complete with submit still enabled.
    turnstileToken.value = undefined
    notification.create({
      title: t('login.errorTitle'),
      description: t('login.captchaUnavailable'),
      type: 'warning',
      duration: 5000,
    })
  }

  const handleSubmit = async () => {
    submitLoading.value = true

    try {
      await formRef.value?.getForm().validate()
      const response = await AuthService.generateOtp({
        altId: store.locationId.value,
        email: formValue.value.email,
        altType: AltType.LOCATION,
        storeId: store.storeId.value,
        turnstileToken: turnstileToken.value,
        turnstileType: turnstileType.value,
      })

      if (!response.data.status) {
        throw new Error(response.data.message)
      }
      contactId.value = response.data?.data?.contactId
      otpSent.value = true
      turnstileError.value = ''
    } catch (error: unknown) {
      console.error(error)
      if (error instanceof AxiosError) {
        const status = error.response?.status
        const err = error.response?.data
        // Only escalate to the visible captcha when the rejection is actually
        // captcha-related. A bare HTTP 401 from upstream (e.g., contact lookup
        // failed) should surface as a normal error toast — solving a captcha
        // wouldn't fix it and silently flipping the widget confuses the user.
        const isCaptchaRejection =
          status === 429 ||
          err?.code === 'CAPTCHA_REQUIRED' ||
          err?.code === 'TURNSTILE_VERIFICATION_FAILED' ||
          err?.message === 'CAPTCHA_REQUIRED' ||
          err?.message === 'TURNSTILE_VERIFICATION_FAILED'
        if (isCaptchaRejection) {
          switchToVisibleChallenge()
        } else {
          notification.create({
            title: t('login.errorTitle'),
            description: getErrorMessage(error, t('login.otpError')),
            type: 'error',
            duration: 5000,
          })
        }
      } else {
        notification.create({
          title: t('login.errorTitle'),
          description: getErrorMessage(error, t('login.otpError')),
          type: 'error',
          duration: 5000,
        })
      }
    } finally {
      submitLoading.value = false
      // Cloudflare Turnstile tokens are single-use; clear the stored token
      // and reset the widget after every submit so the next attempt isn't
      // replaying a stale/consumed token.
      turnstileToken.value = undefined
      turnstileWidgetRef.value?.reset()
    }
  }
</script>

<template>
  <div v-if="!otpSent" class="max-w-96 mx-auto flex flex-col items-center">
    <h1 class="text-2xl font-semibold text-center">{{ store.storeName }}</h1>
    <UITextMdNormal class="mt-2 text-gray-500">{{ t('login.welcomeBack') }}</UITextMdNormal>
    <UIForm :model="formValue" ref="formRef" id="login-form" :rules="rules" class="w-full">
      <UIFormItem id="email-item" label="" path="email">
        <UIInput v-model="formValue.email" id="email" type="email" :placeholder="t('login.emailPlaceholder')" />
      </UIFormItem>
      <TurnstileWidget
        ref="turnstileWidgetRef"
        :mode="turnstileType"
        @verify="onTurnstileVerify"
        @expired="onTurnstileExpired"
        @error="onTurnstileError"
      />
      <div class="mt-2">
        <UIAlert v-if="turnstileError" id="alert" type="error" :closable="false">
          <template #content>{{ turnstileError }}</template>
        </UIAlert>
      </div>
      <UIButton :loading="submitLoading" @click="handleSubmit" class="w-full mt-4" id="login-submit-button" type="primary">
        {{ t('login.button') }}
      </UIButton>
    </UIForm>
  </div>
  <OTPScreen v-else :contactId="contactId" :email="formValue.email" @back-to-login="otpSent = false" @on-login="emit('onLogin')" />
</template>
